<template>
  <div ref="chartContainer" :style="containerStyle" class="sparkline" aria-hidden="true" />
</template>

<script setup>
  /* eslint-disable no-undef */
  import Highcharts from 'highcharts/highstock';
  import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

  const props = defineProps({
    /** Array of [timestamp(ms), value] tuples, {t,v} objects, or plain numbers. */
    data: { type: Array, default: () => [] },
    color: { type: String, default: 'rgb(var(--color-state-success))' },
    height: { type: Number, default: 28 },
    width: { type: [Number, String], default: '100%' },
    /**
     * Render a transparent fill underneath the line. Default is `false` —
     * a thin un-filled line keeps the sparkline a tertiary visual element
     * (per the KPI redesign). Opt in to the filled wash for editorial
     * contexts where the area shape carries meaning (e.g. PulseTile's
     * legacy hero variant).
     */
    showArea: { type: Boolean, default: false },
    /**
     * Drop the chart's internal side/bottom margins so the area fill kisses
     * the container edges. Used by PulseTile to bleed the sparkline into the
     * card's rounded corners. Leaves a 2px top margin so the line itself
     * isn't clipped against the value row above.
     */
    flush: { type: Boolean, default: false },
    /**
     * Render as a step function instead of a smooth line. Use for KPIs
     * whose underlying value is piecewise-constant — DA price per ISP,
     * capacity prices per 4h block, imbalance settlements. Maps directly
     * to Highcharts' `plotOptions.series.step`.
     *   false / null — smooth spline (default)
     *   'left'       — value applies up to the next point (most common
     *                  for prices that hold for the *upcoming* block)
     *   'right'      — value applies from the previous point
     *   'center'     — value applies symmetrically around its timestamp
     */
    step: {
      type: [String, Boolean],
      default: false,
      validator: (value) =>
        value === false || value === null || value === true || ['left', 'center', 'right'].includes(value),
    },
  });

  // Resolve the step prop into the Highcharts-friendly value (`false` or a
  // 'left'/'center'/'right' string). A bare `true` defaults to 'left' which
  // matches the way nearly every European power/gas market publishes block
  // prices (the price covers the *upcoming* delivery interval).
  function resolvedStep() {
    if (props.step === true) return 'left';
    if (typeof props.step === 'string') return props.step;
    return false;
  }

  const chartContainer = ref(null);
  let chartInstance = null;

  const containerStyle = computed(() => ({
    height: `${props.height}px`,
    width: typeof props.width === 'number' ? `${props.width}px` : props.width,
  }));

  // Highcharts' `plotOptions.series.fillOpacity` is silently dropped for some
  // chart types. Setting an explicit alpha-aware `fillColor` is the reliable
  // way to get the wash we want while the line stays at full opacity.
  function colorWithAlpha(color, alpha) {
    if (!color || typeof color !== 'string') {
      return `rgb(var(--color-state-success) / ${alpha})`;
    }

    const trimmed = color.trim();

    if (trimmed.includes('var(') && trimmed.startsWith('rgb') && trimmed[3] === '(') {
      return trimmed.replace(/\)\s*$/, ` / ${alpha})`);
    }

    if (!trimmed.startsWith('#')) {
      return trimmed;
    }

    const clean = trimmed.replace('#', '');
    const expanded = clean.length === 3
      ? clean.split('').map((c) => c + c).join('')
      : clean;
    const r = parseInt(expanded.slice(0, 2), 16);
    const g = parseInt(expanded.slice(2, 4), 16);
    const b = parseInt(expanded.slice(4, 6), 16);
    if ([r, g, b].some((n) => Number.isNaN(n))) {
      return `rgb(var(--color-state-success) / ${alpha})`;
    }
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  function normalizedData() {
    return (props.data || []).map((p, idx) => {
      if (Array.isArray(p)) return p;
      if (p && typeof p === 'object' && 't' in p && 'v' in p) {
        const t = p.t instanceof Date ? p.t.getTime() : new Date(p.t).getTime();
        return [Number.isNaN(t) ? idx : t, Number(p.v)];
      }
      return [idx, Number(p)];
    });
  }

  function buildOptions() {
    const stepValue = resolvedStep();
    // Step series require the non-spline chart type — `areaspline`/`spline`
    // ignore `plotOptions.series.step`. Swap to `area`/`line` when stepping.
    let chartType;
    if (stepValue) {
      chartType = props.showArea ? 'area' : 'line';
    } else {
      chartType = props.showArea ? 'areaspline' : 'spline';
    }
    return {
      chart: {
        type: chartType,
        backgroundColor: 'transparent',
        margin: props.flush ? [2, 0, 0, 0] : [2, 0, 2, 0],
        spacing: [0, 0, 0, 0],
        height: props.height,
        animation: false,
      },
      title: { text: null },
      credits: { enabled: false },
      legend: { enabled: false },
      tooltip: { enabled: false },
      xAxis: {
        visible: false,
        type: 'datetime',
        startOnTick: false,
        endOnTick: false,
        // Push the first / last points hard against the plot edges so the
        // area fill reaches the container border instead of sitting a few
        // pixels inside it.
        minPadding: 0,
        maxPadding: 0,
      },
      yAxis: {
        visible: false,
        startOnTick: false,
        endOnTick: false,
        minPadding: 0,
        maxPadding: 0,
      },
      plotOptions: {
        series: {
          color: props.color,
          lineColor: props.color,
          fillColor: props.showArea ? colorWithAlpha(props.color, 0.18) : 'transparent',
          lineWidth: 1.5,
          step: stepValue || undefined,
          marker: { enabled: false },
          states: { hover: { enabled: false }, inactive: { opacity: 1 } },
          enableMouseTracking: false,
          animation: false,
        },
      },
      series: [{ data: normalizedData() }],
    };
  }

  function render() {
    if (!chartContainer.value) return;
    if (chartInstance) {
      chartInstance.destroy();
      chartInstance = null;
    }
    chartInstance = Highcharts.chart(chartContainer.value, buildOptions());
  }

  onMounted(render);
  onBeforeUnmount(() => {
    if (chartInstance) {
      chartInstance.destroy();
      chartInstance = null;
    }
  });

  watch(
    () => [props.data, props.color, props.height, props.showArea, props.flush, props.step],
    render,
    { deep: true }
  );
</script>

<style scoped>
  .sparkline :deep(.highcharts-background),
  .sparkline :deep(.highcharts-plot-background) {
    fill: transparent !important;
    stroke: none !important;
  }

  .sparkline :deep(.highcharts-container svg rect) {
    border: 0 !important;
    stroke: none !important;
  }
</style>
